import { useState, useCallback, useEffect } from 'react';
import { useAuth } from './useAuth';
import { AuthView, Message } from './useAuthForm';

type AuthViewState = {
  view: AuthView;
  message?: Message;
};

export function useAuthViewState(mustRecoverPassword: boolean, hashMessage: Message) {
  const { isLoading, isAuthenticated } = useAuth();

  const [viewState, setViewState] = useState<AuthViewState>(
    mustRecoverPassword ? { view: 'update_password' } : { view: 'sign_in' },
  );

  const setView = useCallback((view: AuthView) => {
    setViewState({
      view,
      message: null,
    });
  }, []);

  const setMessage = useCallback((message: Message) => {
    setViewState((currentViewState) => ({
      view: currentViewState.view,
      message,
    }));
  }, []);

  // Handle hash message updates
  useEffect(() => {
    if (hashMessage) {
      setViewState((currentViewState) => {
        if (currentViewState.message == null) {
          return {
            view: currentViewState.view,
            message: hashMessage,
          };
        }

        if (currentViewState.message.text !== hashMessage.text) {
          return {
            view: currentViewState.view,
            message: hashMessage,
          };
        }

        return currentViewState;
      });
    }
  }, [hashMessage]);

  // Handle update password validation
  useEffect(() => {
    if (isLoading || isAuthenticated) {
      return;
    }

    if (viewState.view === 'update_password') {
      // We can't update password without a valid session
      setViewState((currentViewState) => {
        const newViewState = {
          message: currentViewState.message ?? {
            type: 'warn' as const,
            text: 'Invalid session - please try again',
          },
          view: 'sign_in' as const,
        };
        if (
          currentViewState.view !== newViewState.view ||
          currentViewState.message !== newViewState.message
        ) {
          return newViewState;
        }
        return currentViewState;
      });
    }
  }, [isLoading, isAuthenticated, viewState]);

  return {
    viewState,
    setView,
    setMessage,
  };
}
